// The AI Assistant's answers as they come. POST apis/chatbot with "stream":
// true answers with server-sent events (python ChatbotResource._stream):
//   status  {stage: "thinking" | "tools" | "writing", tools?}  the work so far
//   visual  {id, kind, ...}  a figure group, chart or table, once its data is in
//   delta   {text}           the answer's text as it is written
//   reset   {}               the text so far dropped (the model turned to tools)
//   done    the answer whole - the JSON a question asked without streaming gets
//   error   {message}
// A server that does not stream answers with that JSON at once: it is taken
// as the "done".

/** The whole events in `buffer` - each "event: name" and "data: {json}"
 * lines, ended by a blank line - and what is left of it, the start of the
 * next. Comment lines (": ...") and events with unreadable data are passed
 * over. */
export function readEvents(buffer) {
  const events = [];
  const text = buffer.replace(/\r\n?/g, "\n");
  let start = 0;
  let end;
  while ((end = text.indexOf("\n\n", start)) !== -1) {
    let name = "message";
    const data = [];
    for (const line of text.slice(start, end).split("\n")) {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    }
    start = end + 2;
    if (!data.length) continue;
    try {
      events.push({ name, data: JSON.parse(data.join("\n")) });
    } catch {
      // Not ours, or cut short: passed over.
    }
  }
  return { events, rest: text.slice(start) };
}

/** An error from the chatbot API: its HTTP status (404 - the conversation
 * is gone), and its message. */
export class ChatError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = "ChatError";
    this.status = status;
  }
}

/**
 * Asks a question and follows its answer as it comes: `onEvent(name, data)`
 * for each event, the last a "done" with the answer whole, which is also
 * what this resolves to. Rejects with a ChatError on a refusal (its status),
 * an "error" event, or a stream that ends without its answer; with an
 * AbortError when `signal` aborts it.
 */
export async function streamChat({ url, headers = {}, body, signal, onEvent = () => {} }) {
  const response = await fetch(url, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json", Accept: "text/event-stream, application/json" },
    body: JSON.stringify({ ...body, stream: true }),
    signal,
  });
  const type = response.headers.get("Content-Type") || "";
  if (!response.ok) {
    let message = `The chatbot answered ${response.status}`;
    try {
      message = (await response.json())?.message || message;
    } catch {
      // No JSON with it.
    }
    throw new ChatError(message, response.status);
  }
  if (!type.includes("text/event-stream") || !response.body) {
    const answer = await response.json();
    onEvent("done", answer);
    return answer;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = null;
  const handle = ({ name, data }) => {
    if (name === "error") throw new ChatError(data?.message || "The answer could not be completed.");
    onEvent(name, data);
    if (name === "done") answer = data;
  };
  for (;;) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    const { events, rest } = readEvents(buffer);
    buffer = rest;
    events.forEach(handle);
    if (done) break;
  }
  // A last event the server did not end with its blank line.
  if (buffer.trim()) readEvents(`${buffer}\n\n`).events.forEach(handle);
  if (!answer) throw new ChatError("The answer stopped before it was complete.");
  return answer;
}
